import React, { useEffect, useRef, useState } from "react";
import "./conversations.scss";
import { Dropdown } from "primereact/dropdown";
import messageExclamation from "../../../assets/message-exclamation.svg";
import EmptyDataView from "../../nodataview";
import PropTypes from "prop-types";
import Message from "./Message";
import MessageInput from "./MessageInput";
import MessageSkeleton from "../../../Pages/Process/components/MessageSkeleton";
import FlaggedMessage from "../../../Pages/Process/components/FlaggedMessage";
import CommentMessage from "../../../Pages/Process/components/CommentMessage";
import { Divider } from "primereact/divider";
import { groupMessagesByDate } from "../../../utils/conversationUtils";
import { useNavigate, useLocation } from "react-router-dom";
import DeleteModal from "./DeleteModal";
import DeleteCommentModal from "./DeleteCommentModal";
import { useSearchParams } from "react-router-dom";
import PayslipProcessingMessage from "~/Pages/Process/components/PayslipProcessingMessage";
import PayslipAwaitingReviewMessage from "~/Pages/Process/components/PayslipAwaitingReviewMessage";
import SuccessNotification from "~/components/entities/address/SuccessNotification";

const Conversations = ({
  title,
  messages = [],
  fetchMessages,
  isMessageLoading,
  formatDate,
  deleteConversation,
  isDeleteLoading,
  setDeleteLoading,
  deleteModal,
  setDeleteModal,
  selectedMessage,
  setSelectedMessage,
  entitydata,
  relatedType,
  serviceData = {},
  requestdata = {},
  requestType = "",
  deleteComment,
  setDeleteComment,
  processdata = {},
  hasMorePrevData,
  hasMoreNextData,
  isDataFetching,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const commentId = searchParams.get("commentId");
  const messageRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const sortOptions = ["Oldest First", "Newest First"];
  const [sortBy, setSortBy] = useState(sortOptions[0]);
  const previousScrollHeight = useRef(0);
  const isLoadingPrev = useRef(false);
  const intentionallyRemovedCommentId = useRef(false);
  const isLoadingNext = useRef(false);
  const hasInitiallyScrolled = useRef(false);
  const didInitialAutoScroll = useRef(false);
  const shouldScrollAfterSend = useRef(false);
  const shouldScrollAfterDelete = useRef(false);
  const isScrollingToTargetMessage = useRef(false);
  const targetMessageScrollTop = useRef(null);
  const isPerformingInitialScroll = useRef(false);

  const sortHandler = (option) => {
    setSortBy(option.target.value);
  };

  // On first load with data, scroll to latest message once; otherwise, let user control
  useEffect(() => {
    if (!isMessageLoading && messages.length > 0) {
      const container = scrollContainerRef.current;

      // Don't auto-scroll if we're scrolling to a specific comment
      if (!didInitialAutoScroll.current && container && !commentId) {
        // Temporarily disable scroll handling to prevent triggering loaders
        hasInitiallyScrolled.current = false;
        container.scrollTop = container.scrollHeight;
        // Re-enable after the programmatic scroll settles
        const t = setTimeout(() => {
          hasInitiallyScrolled.current = true;
          didInitialAutoScroll.current = true;
        }, 150);
        return () => clearTimeout(t);
      }

      // If scrolling to a comment, enable scroll handling but don't auto-scroll
      if (commentId && !hasInitiallyScrolled.current) {
        hasInitiallyScrolled.current = true;
      }

      // Subsequent loads: keep scroll handling enabled; do not auto-scroll
      hasInitiallyScrolled.current = true;
    }
  }, [isMessageLoading, messages.length, commentId]);

  // When a send or delete is initiated, scroll to bottom on the next messages render
  // Track previous loading state to detect when loading finishes
  const prevIsMessageLoading = useRef(false);

  useEffect(() => {
    console.log("Scroll effect triggered:", {
      isMessageLoading,
      prevIsMessageLoading: prevIsMessageLoading.current,
      shouldScrollAfterSend: shouldScrollAfterSend.current,
      shouldScrollAfterDelete: shouldScrollAfterDelete.current,
      hasContainer: !!scrollContainerRef.current,
      messagesLength: messages.length,
    });

    // Only scroll when loading transitions from true to false
    const wasLoading = prevIsMessageLoading.current;
    const isNowLoaded = !isMessageLoading;
    const shouldScroll =
      wasLoading &&
      isNowLoaded &&
      (shouldScrollAfterSend.current || shouldScrollAfterDelete.current);

    console.log("Scroll check:", { wasLoading, isNowLoaded, shouldScroll });

    if (shouldScroll && scrollContainerRef.current) {
      console.log("About to scroll to bottom");
      // Use a longer timeout to ensure the DOM has updated with the new message
      const scrollTimer = setTimeout(() => {
        if (scrollContainerRef.current) {
          const scrollHeight = scrollContainerRef.current.scrollHeight;
          scrollContainerRef.current.scrollTop = scrollHeight;
          console.log("Scrolled to bottom after send/delete", {
            scrollHeight: scrollHeight,
            scrollTop: scrollContainerRef.current.scrollTop,
          });
        }
        shouldScrollAfterSend.current = false;
        shouldScrollAfterDelete.current = false;
      }, 500);

      return () => clearTimeout(scrollTimer);
    }

    // Update previous loading state
    prevIsMessageLoading.current = isMessageLoading;
  }, [isMessageLoading, messages.length]);

  // Scroll position when loading PREVIOUS messages
  // Load more previous messages if content doesn't fill container
  useEffect(() => {
    if (
      !isMessageLoading &&
      messages.length > 0 &&
      hasMorePrevData &&
      !isDataFetching
    ) {
      const checkContentHeight = () => {
        const container = scrollContainerRef.current;
        if (container) {
          const hasScrollbar = container.scrollHeight > container.clientHeight;
          //Scroll logic keeping it for debugging
          // console.log("Content height check:", {
          //   scrollHeight: container.scrollHeight,
          //   clientHeight: container.clientHeight,
          //   hasScrollbar,
          //   messagesCount: messages.length
          // });

          if (!hasScrollbar && hasMorePrevData && !isDataFetching) {
            // console.log("No scrollbar detected - auto-fetching more previous messages");
            loadPreviousMessages();
          }
        }
      };

      // When viewing a comment, check content height immediately
      // Otherwise, use a delay to avoid too many rapid checks
      const delay = commentId ? 100 : 200;
      setTimeout(checkContentHeight, delay);
    }
  }, [
    messages.length,
    isMessageLoading,
    hasMorePrevData,
    isDataFetching,
    commentId,
  ]);

  // Auto-load next messages on initial load to ensure we have future messages
  useEffect(() => {
    if (
      !isMessageLoading &&
      messages.length > 0 &&
      hasMoreNextData &&
      !isDataFetching &&
      (didInitialAutoScroll.current || commentId)
    ) {
      // Auto-load next messages once after initial scroll or when viewing a comment
      const autoLoadNextTimer = setTimeout(() => {
        loadNextMessages();
      }, 300);

      return () => clearTimeout(autoLoadNextTimer);
    }
  }, [
    didInitialAutoScroll.current,
    isMessageLoading,
    messages.length,
    hasMoreNextData,
    isDataFetching,
    commentId,
  ]);

  // Callback to remove commentId when send button becomes enabled
  const onSendButtonEnabled = () => {
    if (commentId && !intentionallyRemovedCommentId.current) {
      intentionallyRemovedCommentId.current = true;
      const params = new URLSearchParams(searchParams);
      params.delete("commentId");
      navigate(`?${params.toString()}`, { replace: true });
    }
  };

  // Scroll to comment if commentId exists
  useEffect(() => {
    if (isMessageLoading) return;

    // If commentId was removed, reset the flag
    if (!commentId && intentionallyRemovedCommentId.current) {
      intentionallyRemovedCommentId.current = false;
      return;
    }

    // If we intentionally removed the commentId, don't scroll to it
    if (intentionallyRemovedCommentId.current) {
      return;
    }

    if (commentId && messageRefs.current[commentId]) {
      // Use a longer timeout to ensure all other effects have settled
      const timeoutId = setTimeout(() => {
        const container = scrollContainerRef.current;
        const targetElement = messageRefs.current[commentId];

        if (container && targetElement) {
          // Use scrollIntoView with center alignment for proper centering
          targetElement.scrollIntoView({ behavior: "auto", block: "center" });

          // Store the target scroll position
          targetMessageScrollTop.current = container.scrollTop;
          isScrollingToTargetMessage.current = false; // Don't keep forcing it
          isPerformingInitialScroll.current = false;
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [commentId, isMessageLoading, messages.length, searchParams]);

  const groupedMessages = groupMessagesByDate(messages || []);

  const allConversationAttachments = (messages || []).flatMap(
    (m) => m.messageData?.attachmentData || [],
  );

  const closeModalHanlder = () => {
    setDeleteModal(false);
    setDeleteLoading(false);
    setSelectedMessage(null);
    setDeleteComment(false);
  };

  const deleteHandler = () => {
    setDeleteLoading(true);
    // After deletion, ensure we scroll to the latest message
    shouldScrollAfterDelete.current = true;
    deleteConversation(selectedMessage?.id);
  };

  // Load PREVIOUS messages (scroll UP)
  const loadPreviousMessages = async () => {
    if (isDataFetching || !hasMorePrevData) {
      return;
    }

    try {
      isLoadingPrev.current = true;

      if (scrollContainerRef.current) {
        previousScrollHeight.current = scrollContainerRef.current.scrollHeight;
      }

      await fetchMessages("FETCHPREVDATA");
    } catch (error) {
      isLoadingPrev.current = false;
    }
  };

  // Load NEXT messages (scroll DOWN)
  const loadNextMessages = async () => {
    if (isDataFetching || !hasMoreNextData) {
      return;
    }

    try {
      isLoadingNext.current = true;

      await fetchMessages("FETCHNEXTDATA");

      isLoadingNext.current = false;
    } catch (error) {
      isLoadingNext.current = false;
    }
  };

  // Handle scroll event
  const handleScroll = (e) => {
    // Ignore programmatic scrolls during initial mount/load or during initial scroll-to-comment
    if (
      isMessageLoading ||
      !hasInitiallyScrolled.current ||
      isPerformingInitialScroll.current
    )
      return;

    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    const topThreshold = 100;
    const bottomThreshold = 100;

    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    // Normal scroll behavior - no special handling for commentId
    // Just let user scroll freely

    // Normal scroll behavior (when not viewing a specific comment OR after user scrolls away from target)
    // Scrolling UP - near TOP
    if (scrollTop < topThreshold && hasMorePrevData && !isDataFetching) {
      loadPreviousMessages();
    }

    // Scrolling DOWN - near BOTTOM
    else if (
      distanceFromBottom < bottomThreshold &&
      hasMoreNextData &&
      !isDataFetching
    ) {
      loadNextMessages();
    }
  };

  return (
    <div
      className={`conversations-layout ${processdata?.escalation ? "process-escalated" : ""}`}
    >
      <div className="conversations-container">
        <div className="title-container">
          {title && <h3 className="title">{title}</h3>}
          <div className="title-sort-section">
            <p className="sort-label">Sort By</p>
            <Dropdown
              value={sortBy}
              options={sortOptions}
              className="sort-options"
              onChange={sortHandler}
              dropdownIcon="pi pi-chevron-up"
            />
          </div>
        </div>

        <div
          className="content-container"
          id="conversation-scroll"
          ref={scrollContainerRef}
          onScroll={handleScroll}
          style={{
            height: "calc(100vh - 300px)",
            overflowY: "auto",
          }}
        >
          {isMessageLoading && (!messages || messages.length === 0) ? (
            <div className="loading-container">
              <MessageSkeleton
                nameWidth="8rem"
                dateWidth="4rem"
                messageWidth="10rem"
              />
              <MessageSkeleton
                nameWidth="10rem"
                dateWidth="6rem"
                messageWidth="20rem"
              />
              <MessageSkeleton
                nameWidth="6rem"
                dateWidth="3rem"
                messageWidth="30rem"
              />
              <MessageSkeleton
                nameWidth="12rem"
                dateWidth="4rem"
                messageWidth="30rem"
              />
            </div>
          ) : messages && messages.length > 0 ? (
            <div>
              {groupedMessages.map((group, index) => (
                <div key={`group-${index}`}>
                  <Divider>
                    <p className="date-group">{group.dateGroup}</p>
                  </Divider>
                  {group.messages.map((message, msgIndex) => {
                    const messageKey =
                      message.id ||
                      message.messageData?.id ||
                      `msg-${index}-${msgIndex}`;

                    return (
                      <React.Fragment key={messageKey}>
                        {message.messageData &&
                          message.type !== "TASK_COMMENTS" &&
                          message.type !== "PAYSLIP_PROCESSING_MESSAGE" &&
                          message.type !== "PAYSLIP_MATCHING_RESULT_MESSAGE" &&
                          message.type !== "PAYSLIP_SUBMITTED_MESSAGE" && (
                            <Message
                              messageInfo={message.messageData}
                              formatDate={formatDate}
                              conversation={message}
                              deletedAt={message.deletedAt}
                              setDeleteModal={setDeleteModal}
                              setSelectedMessage={setSelectedMessage}
                              relatedType={relatedType}
                              processdata={processdata}
                              serviceData={serviceData}
                              allConversationAttachments={
                                allConversationAttachments
                              }
                            />
                          )}

                        {message.type === "TASK_COMMENTS" && (
                          <div
                            ref={(el) => {
                              if (message.messageData?.id) {
                                messageRefs.current[message.messageData.id] =
                                  el;
                              }
                            }}
                          >
                            <CommentMessage
                              messageInfo={message.messageData}
                              formatDate={formatDate}
                              deletedAt={message.deletedAt}
                              setDeleteModal={setDeleteComment}
                              setSelectedMessage={setSelectedMessage}
                              conversation={message}
                              relatedType={relatedType}
                              processdata={processdata}
                              allCommentAttachments={allConversationAttachments}
                            />
                          </div>
                        )}

                        {(message.type === "PAYSLIP_PROCESSING_MESSAGE" ||
                          message.type === "PAYSLIP_SUBMITTED_MESSAGE") && (
                          <PayslipProcessingMessage
                            messageInfo={message.messageData}
                            formatDate={formatDate}
                          />
                        )}

                        {message.type === "PAYSLIP_MATCHING_RESULT_MESSAGE" && (
                          <div
                            ref={(el) => {
                              const payslipId =
                                message?.messageData?.payslipMessageData?.id;
                              if (payslipId) {
                                messageRefs.current[payslipId] = el;
                              }
                            }}
                          >
                            <PayslipAwaitingReviewMessage
                              messageInfo={message.messageData}
                              formatDate={formatDate}
                              fetchMessages={fetchMessages}
                            />
                          </div>
                        )}

                        {message.flagData && (
                          <FlaggedMessage
                            messageInfo={message.flagData}
                            formatDate={formatDate}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              ))}

              {/* Hide skeleton when loading next messages - just show messages */}
            </div>
          ) : (
            groupedMessages.map((group, index) => (
              <div key={index}>
                <Divider>
                  <p className="date-group">{group.dateGroup}</p>
                </Divider>
                {group.messages.map((message) => (
                  <>
                    {message.messageData &&
                      message.type !== "TASK_COMMENTS" &&
                      message.type !== "PAYSLIP_PROCESSING_MESSAGE" &&
                      message.type !== "PAYSLIP_MATCHING_RESULT_MESSAGE" &&
                      message.type !== "PAYSLIP_SUBMITTED_MESSAGE" && (
                        <Message
                          messageInfo={message.messageData}
                          formatDate={formatDate}
                          conversation={message}
                          deletedAt={message.deletedAt}
                          setDeleteModal={setDeleteModal}
                          setSelectedMessage={setSelectedMessage}
                          relatedType={relatedType}
                          processdata={processdata}
                          serviceData={serviceData}
                          allConversationAttachments={
                            allConversationAttachments
                          }
                        />
                      )}
                    {message.type === "TASK_COMMENTS" && (
                      <div
                        ref={(el) =>
                          (messageRefs.current[message.messageData.id] = el)
                        }
                      >
                        <CommentMessage
                          messageInfo={message.messageData}
                          formatDate={formatDate}
                          deletedAt={message.deletedAt}
                          setDeleteModal={setDeleteComment}
                          setSelectedMessage={setSelectedMessage}
                          conversation={message}
                          relatedType={relatedType}
                          onDeleteClick={handleDeleteClick}
                          allCommentAttachments={allConversationAttachments}
                        />
                      </div>
                    )}
                    {(message.type === "PAYSLIP_PROCESSING_MESSAGE" ||
                      message.type === "PAYSLIP_SUBMITTED_MESSAGE") && (
                      <PayslipProcessingMessage
                        messageInfo={message.messageData}
                        formatDate={formatDate}
                      />
                    )}
                    {message.type === "PAYSLIP_MATCHING_RESULT_MESSAGE" && (
                      <div
                        ref={(el) =>
                          (messageRefs.current[
                            message?.messageData?.payslipMessageData?.id
                          ] = el)
                        }
                      >
                        <PayslipAwaitingReviewMessage
                          messageInfo={message.messageData}
                          formatDate={formatDate}
                          fetchMessages={fetchMessages}
                        />
                      </div>
                    )}
                    {message.flagData && (
                      <FlaggedMessage
                        messageInfo={message.flagData}
                        formatDate={formatDate}
                      />
                    )}
                  </>
                ))}
              </div>
            ))
          )}
          {!isMessageLoading && messages?.length === 0 && (
            <div className="empty-state">
              <EmptyDataView
                Icon={
                  <img
                    src={messageExclamation}
                    alt="empty-messages"
                    width={112}
                    height={112}
                  />
                }
                Header="Nothing here yet"
                Description="Add a comment, upload a file, or wait for others."
              />
            </div>
          )}
        </div>
      </div>

      <MessageInput
        fetchMessages={fetchMessages}
        entitydata={entitydata}
        relatedType={relatedType}
        serviceData={serviceData}
        requestdata={requestdata}
        requestType={requestType}
        processdata={processdata}
        allConversationAttachments={allConversationAttachments}
        onRequestScrollToLatest={() => {
          shouldScrollAfterSend.current = true;
        }}
        onSendButtonEnabled={onSendButtonEnabled}
        onBeforeSend={() => {
          // Force scroll to bottom when sending
          shouldScrollAfterSend.current = true;
        }}
      />

      <DeleteModal
        isDeleteLoading={isDeleteLoading}
        deleteMessage={deleteHandler}
        visible={deleteModal}
        closeModal={closeModalHanlder}
      />

      {deleteComment && (
        <DeleteCommentModal
          isDeleteLoading={isDeleteLoading}
          deleteMessage={deleteHandler}
          visible={deleteComment}
          closeModal={closeModalHanlder}
        />
      )}
    </div>
  );
};

Conversations.propTypes = {
  title: PropTypes.string,
  messages: PropTypes.array,
  fetchMessages: PropTypes.func,
  isMessageLoading: PropTypes.bool,
  formatDate: PropTypes.func,
  deleteConversation: PropTypes.func,
  isDeleteLoading: PropTypes.bool,
  setDeleteLoading: PropTypes.func,
  deleteModal: PropTypes.bool,
  setDeleteModal: PropTypes.func,
  selectedMessage: PropTypes.object,
  setSelectedMessage: PropTypes.func,
  relatedType: PropTypes.string,
  serviceData: PropTypes.object,
  requestType: PropTypes.string,
  deleteComment: PropTypes.bool,
  setDeleteComment: PropTypes.func,
  entitydata: PropTypes.object,
  requestdata: PropTypes.object,
  processdata: PropTypes.object,
  hasMorePrevData: PropTypes.bool,
  hasMoreNextData: PropTypes.bool,
  isDataFetching: PropTypes.bool,
};

export default Conversations;
